import { useEffect, useState } from "react";
import { Check, Copy, Loader2, Zap } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Escolha dos eventos que a Zernio vai entregar aqui.
//
// O painel dela oferece a lista inteira; esta tela oferece a mesma, marcando
// quais têm efeito no atendimento. Sem essa marca alguém assina "publicação
// publicada" esperando alguma coisa acontecer e o evento só vira linha de log.

const WebhookModal = ({ open, onClose, url, eventosAtuais, onRegistrar }) => {
  const [grupos, setGrupos] = useState([]);
  const [escolhidos, setEscolhidos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;

    (async () => {
      setCarregando(true);

      try {
        const { data } = await api.get("/zernio/webhook/events");

        setGrupos(data.grupos || []);
        setEscolhidos(
          eventosAtuais?.length ? eventosAtuais : data.padrao || []
        );
      } catch (err) {
        toastError(err);
      }

      setCarregando(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const alternar = (chave) =>
    setEscolhidos((atuais) =>
      atuais.includes(chave)
        ? atuais.filter((item) => item !== chave)
        : [...atuais, chave]
    );

  const registrar = async () => {
    setSalvando(true);
    await onRegistrar(escolhidos);
    setSalvando(false);
    onClose();
  };

  const copiar = () => {
    navigator.clipboard.writeText(url);
  };

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Eventos entregues aqui</DialogTitle>
          <DialogDescription>
            Escolha o que a Zernio deve mandar para este sistema. O que está
            marcado como “tem efeito” muda alguma coisa no atendimento; o resto
            só é registrado.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2.5">
          <code className="min-w-0 flex-1 truncate text-xs">{url}</code>
          <Button
            variant="outline"
            size="icon"
            onClick={copiar}
            aria-label="Copiar endereço"
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        {carregando ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="-mx-1 flex-1 space-y-5 overflow-y-auto px-1 py-1">
            {grupos.map((grupo) => (
              <div key={grupo.grupo}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {grupo.grupo}
                </p>

                <div className="grid gap-1.5 sm:grid-cols-2">
                  {grupo.eventos.map((evento) => (
                    <label
                      key={evento.chave}
                      className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 text-sm hover:bg-accent"
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={escolhidos.includes(evento.chave)}
                        onCheckedChange={() => alternar(evento.chave)}
                      />
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-1.5">
                          {evento.rotulo}
                          {evento.tratado && (
                            <Badge variant="secondary" className="gap-1">
                              <Zap className="h-3 w-3" />
                              tem efeito
                            </Badge>
                          )}
                        </span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">
                          {evento.chave}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="items-center gap-2 sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {escolhidos.length} eventos escolhidos
          </p>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={salvando}>
              Cancelar
            </Button>
            <Button
              onClick={registrar}
              disabled={salvando || escolhidos.length === 0}
            >
              {salvando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Registrar na Zernio
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookModal;
